import { CouponDetailViewModel, InvoiceProductDetailViewModel } from "@payping/api";
import { LOGGED_OUT } from "./constants";
import { LoggedOutAction } from "./types";

export const ADD_ENTRY = "ADD_ENTRY";
export const REMOVE_ENTRY = "REMOVE_ENTRY";
export const UPDATE_ENTRY = "UPDATE_ENTRY";
export const INC_FAST_INVOICE_COUNTER = "INC_FAST_INVOICE_COUNTER";
export const RESET_FAST_INVOICE_COUNTER = "RESET_FAST_INVOICE_COUNTER";
export const SET_ORDER_TYPE = "SET_ORDER_TYPE";
export const TOGGLE_USE_WEIGHT = "TOGGLE_USE_WEIGHT";
export const SET_INVOICE_COUPON = "SET_INVOICE_COUPON";
export const SET_PRODUCT_COUPON = "SET_PRODUCT_COUPON";
export const CLEAR_FASTINVOICE = "CLEAR_FASTINVOICE";

export type OrderType = "salon" | "pack" | "delivery";

export type ActiveDiscountType = "customerClub" | "coupon" | "manual" | undefined;

export interface FastInvoiceState {
  invoice: {
    products: InvoiceProductDetailViewModel[];
    latestSelectedProduct?: InvoiceProductDetailViewModel;
    orderType: OrderType;
    activeDiscountType: ActiveDiscountType;
    invoiceCoupon?: CouponDetailViewModel;
  };
  fastInvoiceCounter: number;
  useWeight: boolean;
}

export interface AddEntryAction {
  type: typeof ADD_ENTRY;
  payload: { product: InvoiceProductDetailViewModel };
}

export interface RemoveEntryAction {
  type: typeof REMOVE_ENTRY;
  payload: { code: string };
}

export interface UpdateEntryAction {
  type: typeof UPDATE_ENTRY;
  payload: { code: string; newProduct: InvoiceProductDetailViewModel };
}

export interface IncFastInvoiceCounterAction {
  type: typeof INC_FAST_INVOICE_COUNTER;
}

export interface ResetFastInvoiceCounterAction {
  type: typeof RESET_FAST_INVOICE_COUNTER;
}

export interface SetOrderTypeAction {
  type: typeof SET_ORDER_TYPE;
  payload: { orderType: OrderType };
}

export interface ToggleUseWeightAction {
  type: typeof TOGGLE_USE_WEIGHT;
}

export interface SetInvoiceCouponAction {
  type: typeof SET_INVOICE_COUPON;
  payload: { coupon: CouponDetailViewModel; type: ActiveDiscountType };
}

export interface SetProductCouponActionPayload {
  itemCode: string;
  coupon: {
    userCouponCode: string;
    discountType: number;
    discountAmount: number;
  };
  type: ActiveDiscountType;
}

export interface SetProductCouponAction {
  type: typeof SET_PRODUCT_COUPON;
  payload: SetProductCouponActionPayload;
}

export interface ClearFastInvoiceAction {
  type: typeof CLEAR_FASTINVOICE;
}

export type FastInvoiceActionTypes =
  | AddEntryAction
  | RemoveEntryAction
  | UpdateEntryAction
  | IncFastInvoiceCounterAction
  | ResetFastInvoiceCounterAction
  | SetOrderTypeAction
  | ToggleUseWeightAction
  | SetInvoiceCouponAction
  | SetProductCouponAction
  | ClearFastInvoiceAction
  | LoggedOutAction;

const initialState: FastInvoiceState = {
  fastInvoiceCounter: 100,
  invoice: {
    activeDiscountType: undefined,
    invoiceCoupon: undefined,
    latestSelectedProduct: undefined,
    orderType: "salon",
    products: [],
  },
  useWeight: false,
};

export default function fastInvoice(state = initialState, action: FastInvoiceActionTypes): FastInvoiceState {
  if (action.type === ADD_ENTRY) {
    const { product } = action.payload;
    const existedItem = state.invoice.products.find((inListItem) => inListItem.code === product.code);
    let newProducts = [];
    if (!existedItem) {
      // product is new
      newProducts = [...state.invoice.products, product];
    } else {
      // product already exist so just increase quantity
      let quantity = (existedItem.quantity || 0) + (product.quantity || 0);
      quantity = Math.round(quantity * 1000) / 1000;
      newProducts = state.invoice.products.map((inListItem) =>
        existedItem.code === inListItem.code ? { ...inListItem, quantity } : inListItem
      );
    }

    return {
      ...state,
      invoice: {
        ...state.invoice,
        latestSelectedProduct: product,
        products: newProducts,
      },
    };
  }
  if (action.type === REMOVE_ENTRY) {
    const { code } = action.payload;
    const newProducts = state.invoice.products.filter((inListItem) => inListItem.code !== code);
    let { latestSelectedProduct } = state.invoice;
    if (latestSelectedProduct && code === latestSelectedProduct.code) {
      latestSelectedProduct = undefined;
    }
    let { invoiceCoupon } = state.invoice;
    if (!newProducts.length) {
      invoiceCoupon = initialState.invoice.invoiceCoupon;
    }

    return {
      ...state,
      invoice: {
        ...state.invoice,
        invoiceCoupon,
        latestSelectedProduct,
        products: newProducts,
      },
    };
  }
  if (action.type === UPDATE_ENTRY) {
    const { code, newProduct } = action.payload;
    const newProducts = state.invoice.products.map((inListItem) =>
      inListItem.code === code ? { ...inListItem, ...newProduct } : inListItem
    );

    return {
      ...state,
      invoice: {
        ...state.invoice,
        latestSelectedProduct: newProduct,
        products: newProducts,
      },
    };
  }

  if (action.type === INC_FAST_INVOICE_COUNTER) {
    return {
      ...state,
      fastInvoiceCounter: state.fastInvoiceCounter + 1,
    };
  }
  if (action.type === RESET_FAST_INVOICE_COUNTER) {
    return {
      ...state,
      fastInvoiceCounter: 100,
    };
  }
  if (action.type === SET_ORDER_TYPE) {
    const { orderType } = action.payload;
    return {
      ...state,
      invoice: {
        ...state.invoice,
        orderType,
      },
    };
  }
  if (action.type === TOGGLE_USE_WEIGHT) {
    return {
      ...state,
      useWeight: !state.useWeight,
    };
  }
  if (action.type === SET_INVOICE_COUPON) {
    const { coupon: invoiceCoupon, type } = action.payload;
    const activeDiscountType = type;

    return {
      ...state,
      invoice: {
        ...state.invoice,
        activeDiscountType,
        invoiceCoupon,
      },
    };
  }
  if (action.type === SET_PRODUCT_COUPON) {
    const { coupon, itemCode, type } = action.payload;

    const couponCode = coupon ? coupon.userCouponCode : "";
    const discountType = coupon ? coupon.discountType : 1;
    const discountAmount = coupon ? coupon.discountAmount : 0;
    const activeDiscountType = type;

    const newProducts = state.invoice.products.map((inListItem) =>
      inListItem.code === itemCode ? { ...inListItem, couponCode, discountAmount, discountType } : inListItem
    );

    return {
      ...state,
      invoice: {
        ...state.invoice,
        activeDiscountType,
        products: newProducts,
      },
    };
  }
  if (action.type === CLEAR_FASTINVOICE) {
    return {
      ...state,
      invoice: initialState.invoice,
    };
  }

  if (action.type === LOGGED_OUT) {
    return initialState;
  }

  return state;
}
